/**
 * Operator flow: subject ID → session info (and headset) → Waterway with live EEG → session finished.
 */
import type { PendingUpload } from "../vendor/myndland-game-lib/dashboard/session/pending.ts";
import { DEFAULT_BRIDGE_URL, EegDevice } from "../vendor/myndland-game-lib/dashboard/eeg/device.ts";
import { EEG_SITES, type SourceKind } from "../vendor/myndland-game-lib/dashboard/eeg/muse/types.ts";
import type { ModeInfo, SubjectStatus } from "../shared/api.ts";
import { parseSubjectId, scheduledSession } from "../shared/schedule.ts";
import { Collector, type FinishedSession, type SessionPlan } from "./collector.ts";

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const screens = ["subject", "info", "play", "done"] as const;
type Screen = typeof screens[number];
let screen: Screen = "subject";

let status: SubjectStatus | null = null;
let overrideNumber: number | null = null;
let finished: FinishedSession | null = null;

const collector = new Collector($("stage"), () => render());
const SETTINGS_KEY = "waterway-muse.headset";

function show(next: Screen) {
  screen = next;
  for (const s of screens) $(`screen-${s}`).hidden = s !== next;
  render();
  if (next === "subject") setTimeout(() => $<HTMLInputElement>("subjectId").focus(), 0);
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const fmtDur = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return h ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`;
};
const fmtDate = (ms: number) => new Date(ms).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });

/* ---------- 1. subject ---------- */
const subjectInput = $<HTMLInputElement>("subjectId"), subjectMsg = $("subjectMsg"), subjectGo = $<HTMLButtonElement>("subjectGo");

$("subjectForm").addEventListener("submit", async (e) => {
  e.preventDefault();
  const id = parseSubjectId(subjectInput.value);
  if (id === null) { setMsg(subjectMsg, "Enter a numeric subject ID (a whole number, 1 or more).", "bad"); return; }
  subjectGo.disabled = true;
  setMsg(subjectMsg, "Looking up the subject’s sessions…");
  try {
    const res = await fetch(`/api/subjects/${id}`, { signal: AbortSignal.timeout(10_000) });
    const body = await res.json().catch(() => null);
    if (!res.ok) throw new Error(body?.error?.message ?? `HTTP ${res.status}`);
    status = body as SubjectStatus;
    overrideNumber = null;
    setMsg(subjectMsg, "");
    renderOverrideOptions();
    (overrideSelect.closest("details") as HTMLDetailsElement).open = false;
    show("info");
  } catch (err) {
    setMsg(subjectMsg, `Could not look up the subject: ${err instanceof Error ? err.message : err}`, "bad");
  } finally {
    subjectGo.disabled = false;
  }
});

function setMsg(el: HTMLElement, text: string, cls: "" | "bad" | "ok" = "") {
  el.textContent = text;
  el.className = `msg ${cls}`;
}

function renderUploads(list: HTMLUListElement, items: PendingUpload[]) {
  list.innerHTML = "";
  for (const item of items) {
    const li = document.createElement("li");
    li.innerHTML = `<span class="name"></span><span class="msg"></span><span class="row"></span>`;
    li.querySelector(".name")!.textContent = `${item.filename} · ${(item.zip.length / 1048576).toFixed(2)} MiB`;
    const msg = li.querySelector<HTMLElement>(".msg")!;
    msg.textContent = item.message;
    const stored = !!item.receipt;
    msg.className = `msg ${stored ? "ok" : !item.simulated && !collector.uploading.has(item.id) && item.message !== "Waiting to upload." ? "bad" : ""}`;
    const btns = li.querySelector(".row")!;
    const button = (label: string, fn: () => void, disabled = false) => {
      const b = document.createElement("button");
      b.className = "btn small"; b.type = "button"; b.textContent = label; b.disabled = disabled;
      b.addEventListener("click", fn);
      btns.appendChild(b);
    };
    if (!item.simulated && !stored) button("Retry", () => void collector.upload(item), collector.uploading.has(item.id) || !item.retryable);
    button("Download", () => collector.download(item));
    if (!stored && collector.pending.includes(item)) button("Discard", () => void collector.discard(item), collector.uploading.has(item.id));
    list.appendChild(li);
  }
}

/* ---------- 2. session info + headset ---------- */
const eSource = $<HTMLSelectElement>("eSource"), eBridgeUrl = $<HTMLInputElement>("eBridgeUrl");
const eConnect = $<HTMLButtonElement>("eConnect"), eDisconnect = $<HTMLButtonElement>("eDisconnect");
const overrideSelect = $<HTMLSelectElement>("overrideSelect");
const infoStart = $<HTMLButtonElement>("infoStart");

try {
  const s = JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? "{}") as { source?: string; bridge?: string };
  if (s.source && [...eSource.options].some((o) => o.value === s.source)) eSource.value = s.source;
  if (s.bridge) eBridgeUrl.value = s.bridge;
} catch (e) { console.warn("[headset] could not read the saved headset settings:", e); }
const saveSettings = () => {
  try { localStorage.setItem(SETTINGS_KEY, JSON.stringify({ source: eSource.value, bridge: eBridgeUrl.value })); } catch { /* per-browser convenience only */ }
};
eSource.addEventListener("change", () => { saveSettings(); render(); });
eBridgeUrl.addEventListener("input", saveSettings);

eConnect.addEventListener("click", () => {
  const kind = eSource.value as SourceKind;
  if (kind === "bluetooth" && !EegDevice.bluetoothAvailable()) {
    $("eMsg").textContent = "This browser has no Web Bluetooth. Use desktop Chrome or Edge on http://localhost (or https).";
    return;
  }
  // No await before connect(): Bluetooth needs this click's user activation for its device chooser.
  collector.device.connect(kind, eBridgeUrl.value.trim() || DEFAULT_BRIDGE_URL)
    .catch((err) => console.error("[eeg] connect failed:", err));
  render();
});
eDisconnect.addEventListener("click", () => void collector.device.disconnect());

overrideSelect.addEventListener("change", () => {
  const n = Number(overrideSelect.value);
  overrideNumber = status?.current && n === status.current.number ? null : n;
  render();
});

$("infoBack").addEventListener("click", () => { status = null; show("subject"); });

/** The session the operator is about to run: the next one, or the corrected number. */
function plan(): SessionPlan | null {
  if (!status) return null;
  const number = overrideNumber ?? status.current?.number ?? null;
  if (number === null) return null;
  const s = scheduledSession(status.subjectId, number, { totalSessions: status.totalSessions, sessionsPerMode: status.sessionsPerMode });
  if (!s) return null;
  const mode = status.modeOrder.find((m) => m.levelId === s.levelId);
  return mode ? { subjectId: status.subjectId, number: s.number, week: s.week, mode } : null;
}

infoStart.addEventListener("click", () => {
  const p = plan();
  if (!p) return;
  try {
    collector.start(p);
    finished = null;
    show("play");
  } catch (err) {
    setMsg($("infoMsg"), err instanceof Error ? err.message : String(err), "bad");
  }
});

function renderOverrideOptions() {
  if (!status) return;
  const want = String(overrideNumber ?? status.current?.number ?? "");
  const done = new Set(status.completed.map((r) => r.number));
  overrideSelect.innerHTML = "";
  if (!status.current && overrideNumber === null) overrideSelect.add(new Option("—", "", true, true));
  for (let n = 1; n <= status.totalSessions; n++) {
    const label = `${n}${status.current?.number === n ? " (next)" : done.has(n) ? " (done)" : ""}`;
    overrideSelect.add(new Option(label, String(n), false, String(n) === want));
  }
}

function modeHtml(p: SessionPlan, st: SubjectStatus): string {
  const doneWeeks = new Set<number>();
  for (const r of st.completed) {
    const s = scheduledSession(st.subjectId, r.number, { totalSessions: st.totalSessions, sessionsPerMode: st.sessionsPerMode });
    if (s) doneWeeks.add(s.week);
  }
  const weeks = Math.ceil(st.totalSessions / st.sessionsPerMode);
  const order = Array.from({ length: weeks }, (_, i) => st.modeOrder[i % st.modeOrder.length]!);
  const sessionInWeek = ((p.number - 1) % st.sessionsPerMode) + 1;
  return `<div class="kicker">Level for this session</div>
    <b>${esc(p.mode.title)}</b>
    <span class="sub">${esc(p.mode.subtitle)}</span>
    <span class="muted small">Week ${p.week} of ${weeks} · session ${sessionInWeek} of ${st.sessionsPerMode} on this level</span>
    <ol aria-label="Level order for this subject">${order.map((m: ModeInfo, i) =>
      `<li class="${i + 1 === p.week ? "now" : doneWeeks.has(i + 1) ? "done" : ""}">W${i + 1} ${esc(m.title)}</li>`).join("")}</ol>`;
}

function renderInfo() {
  const st = status;
  if (!st) return;
  const p = plan();
  $("infoSubject").textContent = String(st.subjectId);
  $("infoSession").innerHTML = p ? `${p.number}<small> of ${st.totalSessions}</small>` : `Done<small> ${st.totalSessions}/${st.totalSessions}</small>`;
  $("infoMode").innerHTML = p ? modeHtml(p, st) : `<b>Program complete</b><span class="muted small">All ${st.totalSessions} sessions are recorded for this subject. Pick a session under “Wrong session?” only to re-run one.</span>`;
  const last = st.completed.at(-1);
  const notes = [`${st.completed.length} of ${st.totalSessions} sessions completed${last ? `, the last on ${fmtDate(last.startedAt)}` : ""}.`];
  if (overrideNumber !== null) notes.push(`Corrected by the operator: the next session in the record is ${st.current?.number ?? "none (complete)"}.`);
  $("infoNote").textContent = notes.join(" ");
  renderHeadset();
}

function renderHeadset() {
  const p = plan();
  const d = collector.device.snapshot;
  const phase = d.status.phase;
  const eState = $("eState");
  eState.textContent = phase === "idle" || phase === "closed" ? "off" : phase;
  eState.className = `pill ${phase}`;
  $("eMsg").textContent = [d.status.deviceName, d.status.detail, d.battery != null ? `battery ${Math.round(d.battery)}%` : ""].filter(Boolean).join(" · ") || "Not connected.";
  eBridgeUrl.hidden = eSource.value !== "bridge";
  eSource.disabled = eBridgeUrl.disabled = d.connected;
  eConnect.disabled = d.connected || phase === "requesting" || phase === "connecting";
  eDisconnect.disabled = !d.connected;
  $("eContact").innerHTML = chips();

  const simulated = collector.device.info?.simulated;
  let msg = "", cls: "" | "bad" | "ok" = "";
  if (!p) msg = "Nothing to run: the program is complete.";
  else if (!d.streaming) msg = "Put the headset on the participant and connect it.";
  else if (simulated) { msg = "Simulated EEG: this session will not be uploaded and will not count toward the subject’s progress."; cls = "bad"; }
  else { msg = "Ready. Check that the electrode chips are green, then start."; cls = "ok"; }
  setMsg($("infoMsg"), msg, cls);
  infoStart.disabled = !p || !d.streaming || collector.active || collector.packaging;
}

function chips(): string {
  const d = collector.device.snapshot;
  if (!d.connected) return "";
  return EEG_SITES.map((site) => {
    const c = d.contact.find((x) => x.site === site);
    const st = c?.status ?? "unknown";
    return `<span class="chip ${st}" title="${site}: ${st}${c?.acRmsUv != null ? `, ${c.acRmsUv.toFixed(1)} µV RMS` : ""}">${site}</span>`;
  }).join("");
}

/* ---------- 3. play ---------- */
const playProbe = $<HTMLButtonElement>("playProbe"), playEnd = $<HTMLButtonElement>("playEnd");
playProbe.addEventListener("click", () => collector.showProbe());
playEnd.addEventListener("click", async () => {
  const snap = collector.snapshot();
  if (!snap) return;
  const note = snap.gameplayMs === null ? "\n\nGameplay has not started yet, so this session will NOT count toward the subject’s progress." : "";
  if (!confirm(`End this session and upload the recording?${note}`)) return;
  playEnd.disabled = true;
  finished = await collector.end(snap.gameplayMs === null ? "aborted-before-gameplay" : "ended-by-operator");
  playEnd.disabled = false;
  show("done");
});

function renderPlay() {
  const p = collector.plan, snap = collector.snapshot();
  if (!p || !snap) return;
  $("playWho").textContent = `Subject ${p.subjectId} · session ${p.number}${status ? ` of ${status.totalSessions}` : ""}`;
  $("playMode").textContent = `${p.mode.title} · ${p.mode.subtitle}`;
  const label = snap.phase === "calibrated" ? "waiting for Begin" : snap.phase;
  const pill = $("playPhase");
  pill.textContent = label;
  pill.className = `pill ${snap.phase}`;
  $("playClock").textContent = fmtDur(snap.elapsedMs);
  $("playClock").title = snap.gameplayMs !== null ? `Gameplay ${fmtDur(snap.gameplayMs)} · ${snap.samples.toLocaleString()} EEG samples · ${snap.decisions} decisions` : "Session time";
  $("playChips").innerHTML = chips();
  const d = collector.device.snapshot;
  $("playWarn").textContent = d.streaming ? "" : `Headset ${d.status.phase}: EEG is not being recorded`;
  playProbe.disabled = snap.phase !== "gameplay" || collector.probing;
}

/* ---------- 4. done ---------- */
$("doneNext").addEventListener("click", () => {
  finished = null;
  status = null;
  subjectInput.value = "";
  show("subject");
});

function renderDone() {
  const f = finished;
  if (!f) {
    $("doneTitle").textContent = collector.packaging ? "Packaging the recording…" : "Session ended";
    return;
  }
  $("doneTitle").textContent = `Subject ${f.plan.subjectId} · session ${f.plan.number}`;
  const rows: [string, string][] = [
    ["Level", `${f.plan.mode.title} (${f.plan.mode.moduleName})`],
    ["Session ID", f.sessionId],
    ["Duration", fmtDur(f.durationMs)],
    ["Gameplay", f.gameplayMs === null ? "not started" : fmtDur(f.gameplayMs)],
    ["EEG samples", f.samples.toLocaleString()],
    ["Decisions", String(f.decisions)],
    ["Alpha windows", String(f.windows)],
  ];
  const counted = f.gameplayMs !== null && !f.simulated;
  rows.push(["Progress", counted ? `counts as session ${f.plan.number}` : f.simulated ? "simulated: does not count" : "aborted before gameplay: does not count"]);
  if (f.ledgerError) rows.push(["Session record", `NOT saved (${f.ledgerError}). Note the session number by hand.`]);
  if (f.error) rows.push(["Recording", `could not be packaged: ${f.error}`]);
  $("doneStats").innerHTML = rows.map(([k, v]) => `<dt>${esc(k)}</dt><dd>${esc(v)}</dd>`).join("");
  renderUploads($<HTMLUListElement>("doneUpload"), f.item ? [f.item] : []);
}

/* ---------- rendering ---------- */
function render() {
  if (screen === "subject") {
    const box = $("pendingBox");
    box.hidden = collector.pending.length === 0;
    if (!box.hidden) renderUploads($<HTMLUListElement>("pendingList"), collector.pending);
  } else if (screen === "info") renderInfo();
  else if (screen === "play") renderPlay();
  else renderDone();
}

// Contact quality changes without a status event: refresh the chips while the headset screen is up.
setInterval(() => { if (screen === "info") renderHeadset(); }, 1000);

show("subject");
